import { useColorMode, ColorMode } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';
import { useCallback } from 'react';

import { ColorSchemeStruct, Sizes, SizesStruct } from './types';

// Exports
/** Convert numeric rem value to numeric pixel value */
export const remToPx = (rem: number) => rem * 16;

/** Helper to flip between values based on the standard `size` prop */
export function size<Value>({ sm, md, lg }: Record<Sizes, Value>) {
  return (props: { size: unknown }) => {
    const parsedSize = SizesStruct.parse(props.size);
    switch (parsedSize) {
      case 'sm': {
        return sm;
      }
      case 'md': {
        return md;
      }
      case 'lg': {
        return lg;
      }
      default: {
        throw Error('Unreacheable');
      }
    }
  };
}

const resolverCache = new Map<string, string>();
type ResolveStyleProps = { colorMode: ColorMode; colorScheme?: string };
type ResolveValuePair = { light: string; dark: string };
/**
 * When working with colors, we constantly have to:
 * 1. pick between light and dark mode colors
 * 2. look up color values based on the color scheme
 *
 * This helper standardizes this kind of "color value resolution". Since this helper can be called
 * multiple times by multiple components on every render, the results are cached.
 */
export const resolveColor = (
  styleProps: ResolveStyleProps,
  valuePair: ResolveValuePair,
) => {
  // Check for cached value
  const cacheKey = [
    styleProps.colorMode,
    styleProps.colorScheme ?? 'gray',
    valuePair.light,
    valuePair.dark,
  ].join(':');
  const cachedResult = resolverCache.get(cacheKey);
  if (cachedResult) return cachedResult;

  // Resolve dark vs light mode values
  let resolvedValue = mode(valuePair.light, valuePair.dark)(styleProps);

  // No cached value, check for common replacement values, stick value in cache
  if (styleProps.colorScheme) {
    resolvedValue = resolvedValue.replaceAll(
      `{colorScheme}`,
      ColorSchemeStruct.parse(styleProps.colorScheme),
    );
  }
  resolverCache.set(cacheKey, resolvedValue);
  return resolvedValue;
};

export const createResolveColor = (styleProps: ResolveStyleProps) => (
  valuePair: ResolveValuePair,
) => resolveColor(styleProps, valuePair);

export const useSimpleColorMode = () => {
  const { colorMode } = useColorMode();
  return useCallback(
    (light: string, dark: string) =>
      resolveColor({ colorMode }, { light, dark }),
    [colorMode],
  );
};
